import React, { useEffect, useMemo, useRef, useState } from "react";
import "../../assets/css/chat/planWorkspace.css";
import {
  buildAppRequestFromPlan,
  buildPlanDocumentFromMessages,
  extractPlanDocumentFromWs,
  getPlanWorkspaceProgress,
  hasMeaningfulPlanDocument,
  isPlanAppType,
  isProjectInPlanMode,
  mergePlanDocuments,
  PLAN_CHAT_MESSAGES_EVENT,
  PLAN_WORKSPACE_MODE_EVENT,
  resolveArchitectureChart,
  resolveBuildAvailable,
  resolveErdChart,
  resolvePlanBuildPrompt,
  resolvePlanId,
  readComposerMode,
  readPlanMeta,
  writeComposerMode,
  writePlanWorkspaceBuildMode,
  writePlanWorkspaceViewOpen,
} from "./planUtils";

const TABS = [
  { id: "overview", label: "Plan Overview", icon: "fa-solid fa-list-check" },
  { id: "architecture", label: "Architecture", icon: "fa-solid fa-diagram-project" },
  { id: "erd", label: "ERD", icon: "fa-solid fa-database" },
  { id: "agents", label: "Agents", icon: "fa-solid fa-robot" },
  { id: "implementation", label: "Implementation", icon: "fa-solid fa-timeline" },
  { id: "credits", label: "Credit Estimation", icon: "fa-solid fa-coins" },
  { id: "details", label: "Details", icon: "fa-solid fa-shield-halved" },
];

const STATUS_LABEL = {
  pending: "Pending",
  current: "In Progress",
  completed: "Completed",
  testing: "Testing",
  failed: "Failed",
  blocked: "Blocked",
};

const stripMarks = (value = "") =>
  String(value).replace(/\*\*/g, "").replace(/__/g, "").replace(/`/g, "").trim();

const quoteMermaidLabel = (label = "") => {
  const text = String(label).trim();
  if (!text) return text;
  if (
    (text.startsWith('"') && text.endsWith('"')) ||
    (text.startsWith("'") && text.endsWith("'"))
  ) {
    return text;
  }
  if (/[\s/\\().,:;+\-*&<>'#]/.test(text)) {
    return `"${text.replace(/"/g, "'")}"`;
  }
  return text;
};

const sanitizeMermaidChart = (input = "") => {
  let chart = String(input || "")
    .replace(/^[`~]{2,}\s*mermaid\s*/i, "")
    .replace(/[`~]{2,}\s*$/i, "")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/\r\n/g, "\n")
    .trim();
  if (!chart) return "";

  const kind = (chart.match(/^(flowchart|graph|erDiagram|sequenceDiagram)\b/i) || [])[1] || "";
  if (/^erDiagram$/i.test(kind) || /^sequenceDiagram$/i.test(kind)) {
    return chart;
  }

  chart = chart.replace(/\b([A-Za-z][\w-]*)\[\(([^)\]]+)\)\]/g, (_, id, label) => `${id}[(${quoteMermaidLabel(label)})]`);
  chart = chart.replace(/\b([A-Za-z][\w-]*)\(\[([^\]]+)\]\)/g, (_, id, label) => `${id}([${quoteMermaidLabel(label)}])`);
  chart = chart.replace(/\b([A-Za-z][\w-]*)\(\(([^)]+)\)\)/g, (_, id, label) => `${id}((${quoteMermaidLabel(label)}))`);
  chart = chart.replace(/\b([A-Za-z][\w-]*)\{\{([^}]+)\}\}/g, (_, id, label) => `${id}{{${quoteMermaidLabel(label)}}}`);
  chart = chart.replace(/\b([A-Za-z][\w-]*)\{([^{}\n]+)\}/g, (_, id, label) => `${id}{${quoteMermaidLabel(label)}}`);
  chart = chart.replace(/\b([A-Za-z][\w-]*)\[(?!["'(])([^\]]+)\]/g, (_, id, label) => `${id}[${quoteMermaidLabel(label)}]`);
  return chart;
};

let mermaidLoader = null;
let mermaidRenderSeq = 0;

const loadMermaid = () => {
  if (!mermaidLoader) {
    mermaidLoader = import("mermaid").then((mod) => {
      const mermaid = mod.default;
      mermaid.initialize({
        startOnLoad: false,
        securityLevel: "loose",
        suppressErrorRendering: true,
        theme: "base",
        themeVariables: {
          primaryColor: "#dbeafe",
          primaryTextColor: "#0f172a",
          primaryBorderColor: "#60a5fa",
          lineColor: "#64748b",
          secondaryColor: "#ecfdf5",
          tertiaryColor: "#f8fafc",
          fontFamily: "inherit",
        },
        flowchart: { htmlLabels: false, curve: "basis" },
      });
      return mermaid;
    });
  }
  return mermaidLoader;
};

const MermaidBlock = ({ chart }) => {
  const ref = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!chart) return undefined;
    setFailed(false);
    (async () => {
      try {
        const mermaid = await loadMermaid();
        const cleaned = sanitizeMermaidChart(chart);
        if (!cleaned) {
          if (!cancelled) setFailed(true);
          return;
        }
        if (typeof mermaid.parse === "function") {
          await mermaid.parse(cleaned);
        }
        const id = `plan-mmd-${++mermaidRenderSeq}`;
        document.getElementById(id)?.remove();
        document.getElementById(`d${id}`)?.remove();
        const { svg } = await mermaid.render(id, cleaned);
        if (/syntax error/i.test(String(svg || ""))) {
          throw new Error("mermaid syntax error");
        }
        if (!cancelled && ref.current) {
          ref.current.innerHTML = svg;
        }
      } catch {
        if (!cancelled) {
          setFailed(true);
          if (ref.current) ref.current.innerHTML = "";
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [chart]);

  if (!chart) return null;
  if (failed) {
    return <p className="sa-plan-ws-empty">Diagram could not be rendered.</p>;
  }
  return <div className="sa-plan-ws-mermaid" ref={ref} />;
};

const SectionCard = ({ icon, title, badge, children, emptyText }) => {
  if (!children) {
    return (
      <section className="sa-plan-ws-card sa-plan-ws-card--empty">
        <div className="sa-plan-ws-card-head">
          <i className={icon} aria-hidden="true" />
          <h3>{title}</h3>
        </div>
        <p className="sa-plan-ws-empty">{emptyText || "Waiting for details…"}</p>
      </section>
    );
  }
  return (
    <section className="sa-plan-ws-card">
      <div className="sa-plan-ws-card-head">
        <i className={icon} aria-hidden="true" />
        <h3>{title}</h3>
        {badge ? <span className="sa-plan-ws-arch-level">{badge}</span> : null}
      </div>
      <div className="sa-plan-ws-card-body">{children}</div>
    </section>
  );
};

const BulletList = ({ items = [], tone = "default" }) => {
  if (!items.length) return null;
  return (
    <ul className={`sa-plan-ws-bullets sa-plan-ws-bullets--${tone}`}>
      {items.map((item, index) => (
        <li key={`${item}-${index}`}>
          <i
            className={
              tone === "excluded"
                ? "fa-solid fa-xmark"
                : "fa-solid fa-check"
            }
            aria-hidden="true"
          />
          <span>{stripMarks(item)}</span>
        </li>
      ))}
    </ul>
  );
};

const RichText = ({ text }) => {
  if (!text) return null;
  const lines = String(text).split(/\n/).map((line) => line.trim()).filter(Boolean);
  const bullets = lines.filter((line) => /^[-*•]/.test(line));
  if (bullets.length >= 2 && bullets.length === lines.length) {
    return <BulletList items={bullets.map((line) => line.replace(/^[-*•]\s*/, ""))} />;
  }
  return String(text)
    .split(/\n\n+/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block, index) => (
      <p key={index}>{stripMarks(block.replace(/^[-*•]\s+/gm, ""))}</p>
    ));
};

const PlanningProgress = ({ progress }) => (
  <div className="sa-plan-ws-progress-panel" role="status" aria-live="polite">
    <div className="sa-plan-ws-progress-hero">
      <div className="sa-plan-ws-orbit" aria-hidden="true">
        <i className="fa-solid fa-list-check" />
      </div>
      <div>
        <h2>Planning your project</h2>
        <p>Turning your requirements into a structured build plan.</p>
      </div>
      <span className="sa-plan-ws-progress-meta">{progress.percent}%</span>
    </div>
    <div className="sa-plan-ws-progress-bar" aria-hidden="true">
      <span style={{ width: `${progress.percent}%` }} />
    </div>
    <ul className="sa-plan-ws-progress-steps">
      {progress.steps.map((step) => (
        <li key={step.key} className={`is-${step.state}`}>
          <span className="sa-plan-ws-step-mark" aria-hidden="true">
            {step.state === "done" ? (
              <i className="fa-solid fa-check" />
            ) : step.state === "current" ? (
              <i className="fa-solid fa-circle-notch fa-spin" />
            ) : (
              <i className="fa-regular fa-circle" />
            )}
          </span>
          <span>{step.label}</span>
        </li>
      ))}
    </ul>
  </div>
);

const OverviewTab = ({ doc }) => (
  <div className="sa-plan-ws-grid">
    <SectionCard icon="fa-solid fa-lightbulb" title="Project Overview" emptyText="Overview will appear as planning progresses.">
      {doc.overview ? <RichText text={doc.overview} /> : null}
    </SectionCard>

    <SectionCard icon="fa-solid fa-bullseye" title="Objective" emptyText="Objective is being defined…">
      {doc.objective ? <RichText text={doc.objective} /> : null}
    </SectionCard>

    <SectionCard icon="fa-solid fa-layer-group" title="Scope">
      {(doc.scope?.included?.length || doc.scope?.excluded?.length) ? (
        <>
          {doc.scope.included?.length ? (
            <>
              <h4 className="sa-plan-ws-subtitle">Included</h4>
              <BulletList items={doc.scope.included} />
            </>
          ) : null}
          {doc.scope.excluded?.length ? (
            <>
              <h4 className="sa-plan-ws-subtitle">Excluded</h4>
              <BulletList items={doc.scope.excluded} tone="excluded" />
            </>
          ) : null}
        </>
      ) : null}
    </SectionCard>

    <SectionCard icon="fa-solid fa-star" title="Key Features">
      {doc.features?.length ? (
        <div className="sa-plan-ws-feature-grid">
          {doc.features.map((feature, index) => (
            <div key={`${feature}-${index}`} className="sa-plan-ws-feature">
              <i className="fa-solid fa-star" aria-hidden="true" />
              <span>{stripMarks(feature)}</span>
            </div>
          ))}
        </div>
      ) : null}
    </SectionCard>

    <SectionCard icon="fa-solid fa-users" title="Users & Roles">
      {doc.users?.length ? <BulletList items={doc.users} /> : null}
    </SectionCard>

    <SectionCard icon="fa-solid fa-window-maximize" title="Screens / Pages & Flows">
      {doc.screens?.length ? <BulletList items={doc.screens} /> : null}
    </SectionCard>

    <SectionCard icon="fa-solid fa-cubes" title="Technology Stack">
      {doc.techStack?.length ? (
        <div className="sa-plan-ws-tech-grid">
          {doc.techStack.map((item, index) => (
            <div key={`${item.label}-${index}`} className="sa-plan-ws-tech">
              <span className="sa-plan-ws-tech-label">{stripMarks(item.label)}</span>
              <span className="sa-plan-ws-tech-value">{stripMarks(item.value)}</span>
            </div>
          ))}
        </div>
      ) : null}
    </SectionCard>
  </div>
);

const ArchitectureTab = ({ doc }) => {
  const modules = (doc.architectureModules || []).filter(
    (item) => item?.title && (item.chart || item.breakdown?.length || item.notes)
  );
  if (modules.length) {
    return (
      <div className="sa-plan-ws-grid">
        {modules.map((item, index) => (
          <SectionCard
            key={`${item.title}-${index}`}
            icon="fa-solid fa-diagram-project"
            title={item.title}
            badge={item.level || null}
          >
            {item.chart ? <MermaidBlock chart={item.chart} /> : null}
            {item.breakdown?.length ? (
              <>
                <h4 className="sa-plan-ws-subtitle">Breakdown</h4>
                <BulletList items={item.breakdown} />
              </>
            ) : null}
            {item.notes ? <RichText text={item.notes} /> : null}
          </SectionCard>
        ))}
      </div>
    );
  }

  const chart = resolveArchitectureChart(doc);
  const extras = (doc.extraMermaid || []).filter((extra) => {
    const cleaned = String(extra || "").trim();
    if (!cleaned || cleaned === chart) return false;
    return !/^erDiagram\b/i.test(cleaned);
  });
  const showFlow = !chart && doc.architecture?.length && !doc.architecture.some((node) => /mermaid|flowchart|erDiagram|-->/.test(node));
  return (
    <div className="sa-plan-ws-grid">
      <SectionCard icon="fa-solid fa-diagram-project" title="System Architecture" emptyText="Architecture will appear once designed.">
        {chart || showFlow || doc.architectureNotes || extras.length ? (
          <>
            {chart ? <MermaidBlock chart={chart} /> : null}
            {showFlow ? (
              <div className="sa-plan-ws-flow">
                {doc.architecture.map((node, index) => (
                  <React.Fragment key={`${node}-${index}`}>
                    <div className="sa-plan-ws-flow-node">{stripMarks(node)}</div>
                    {index < doc.architecture.length - 1 ? (
                      <div className="sa-plan-ws-flow-arrow" aria-hidden="true">
                        <i className="fa-solid fa-arrow-down" />
                      </div>
                    ) : null}
                  </React.Fragment>
                ))}
              </div>
            ) : null}
            {doc.architectureNotes ? <RichText text={doc.architectureNotes} /> : null}
            {extras.map((extra, index) => (
              <MermaidBlock key={`extra-${index}`} chart={extra} />
            ))}
          </>
        ) : null}
      </SectionCard>
    </div>
  );
};

const ErdTab = ({ doc }) => {
  const chart = resolveErdChart(doc);
  return (
    <div className="sa-plan-ws-grid">
      <SectionCard
        icon="fa-solid fa-database"
        title="Database / ERD"
        emptyText="No persistent data model in this plan."
      >
        {chart || doc.erdNotes ? (
          <>
            {chart ? <MermaidBlock chart={chart} /> : null}
            {doc.erdNotes ? <RichText text={doc.erdNotes} /> : null}
          </>
        ) : null}
      </SectionCard>
    </div>
  );
};

const AgentsTab = ({ doc }) => (
  <div className="sa-plan-ws-grid">
    <SectionCard icon="fa-solid fa-robot" title="Agents / AI Components" emptyText="Agent details will appear when available.">
      {doc.agents?.length ? (
        <div className="sa-plan-ws-agent-grid">
          {doc.agents.map((agent) => (
            <div key={agent.id} className="sa-plan-ws-agent">
              <div className="sa-plan-ws-agent-name">{stripMarks(agent.name)}</div>
              {agent.responsibility ? <p>{stripMarks(agent.responsibility)}</p> : null}
              {agent.tools?.length ? (
                <div className="sa-plan-ws-tool-row">
                  {agent.tools.map((tool) => (
                    <span key={tool} className="sa-plan-ws-chip">{stripMarks(tool)}</span>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : doc.agentsNote ? (
        <p>{stripMarks(doc.agentsNote)}</p>
      ) : null}
    </SectionCard>
  </div>
);

const ImplementationTab = ({ doc }) => (
  <div className="sa-plan-ws-grid">
    <SectionCard icon="fa-solid fa-timeline" title="Implementation Plan" emptyText="Tasks will appear once the plan is ready.">
      {doc.milestones?.length ? (
        <ol className="sa-plan-ws-timeline sa-plan-ws-timeline--tasks">
          {doc.milestones.map((item, index) => (
            <li key={item.id || index} className={`is-${item.status || "pending"}`}>
              <span className="sa-plan-ws-timeline-index">{String(index + 1).padStart(2, "0")}</span>
              <div>
                <strong>{stripMarks(item.title)}</strong>
                {item.tasks?.length ? <BulletList items={item.tasks} /> : item.detail ? <RichText text={item.detail} /> : null}
                <span className={`sa-plan-ws-task-status is-${item.status || "pending"}`}>
                  {STATUS_LABEL[item.status] || "Pending"}
                </span>
              </div>
            </li>
          ))}
        </ol>
      ) : null}
    </SectionCard>
  </div>
);

const CREDIT_DISCLAIMER =
  "Estimated based on the current plan, including requirements, business logic, services, UI complexity, and expected build iterations. Actual usage may vary as the application evolves and the scope changes.";

const resolveCreditSummary = (text = "") => {
  const lines = String(text || "")
    .split(/\n/)
    .map((line) => stripMarks(line.replace(/^>\s?/, "").trim()))
    .filter(Boolean);

  const preferred = lines.find((line) =>
    /estimated\s+application\s+build\s+consumption/i.test(line)
  );
  if (preferred) return preferred.replace(/\*+/g, "").trim();

  const firstMonth = [...lines]
    .reverse()
    .find((line) => /estimated\s+first[- ]month/i.test(line) && /~\s*[\d.,]+/.test(line));
  if (firstMonth) {
    const match = firstMonth.match(/~\s*[\d.,]+\s*(?:[–-]\s*[\d.,]+)?\s*credits?/i);
    if (match) return `Estimated application build consumption: ${match[0]}`;
  }

  const creditLine = [...lines]
    .reverse()
    .find(
      (line) =>
        /~\s*[\d.,]+\s*(?:[–-]\s*[\d.,]+)?\s*credits?\b/i.test(line) &&
        !/\b(hour|token|vCPU|GB|\/mo)\b/i.test(line)
    );
  if (creditLine) {
    const match = creditLine.match(/~\s*[\d.,]+\s*(?:[–-]\s*[\d.,]+)?\s*credits?/i);
    if (match) return `Estimated application build consumption: ${match[0]}`;
  }

  return null;
};

const CreditsTab = ({ doc }) => {
  const summary = resolveCreditSummary(doc.creditEstimation || "");
  return (
    <div className="sa-plan-ws-grid">
      <SectionCard
        icon="fa-solid fa-coins"
        title="Credit Estimation"
        emptyText="Credit estimation will appear once the plan is ready."
      >
        {summary ? (
          <div className="sa-plan-ws-credit">
            <p className="sa-plan-ws-credit-summary">
              <strong>{summary}</strong>
            </p>
            <p className="sa-plan-ws-credit-note">{CREDIT_DISCLAIMER}</p>
          </div>
        ) : null}
      </SectionCard>
    </div>
  );
};

const DetailsTab = ({ doc }) => (
  <div className="sa-plan-ws-grid">
    {(doc.security?.length || doc.assumptions?.length || doc.risks?.length) ? (
      <div className="sa-plan-ws-grid-2">
        {doc.security?.length ? (
          <SectionCard icon="fa-solid fa-shield-halved" title="Security Considerations">
            <BulletList items={doc.security} />
          </SectionCard>
        ) : null}
        {doc.assumptions?.length ? (
          <SectionCard icon="fa-solid fa-circle-info" title="Assumptions">
            <BulletList items={doc.assumptions} />
          </SectionCard>
        ) : null}
        {doc.risks?.length ? (
          <SectionCard icon="fa-solid fa-triangle-exclamation" title="Risks / Considerations">
            <BulletList items={doc.risks} tone="excluded" />
          </SectionCard>
        ) : null}
      </div>
    ) : (
      <SectionCard icon="fa-solid fa-circle-info" title="Details" emptyText="Security, assumptions, and risks will appear when available.">
        {null}
      </SectionCard>
    )}
  </div>
);

const PlanWorkspace = ({
  projectData,
  chatMessages = null,
  onStartBuilding,
  onUpdatePlan,
  isChatVisible,
  onToggleChat,
}) => {
  const projectId = projectData?.id;
  const [tab, setTab] = useState("overview");
  const [liveMessages, setLiveMessages] = useState(chatMessages || projectData?.messages || []);
  const [livePlanDoc, setLivePlanDoc] = useState(null);
  const [modeTick, setModeTick] = useState(0);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refineInput, setRefineInput] = useState("");

  useEffect(() => {
    setLiveMessages(chatMessages || projectData?.messages || []);
  }, [chatMessages, projectData?.messages, projectData?.id]);

  useEffect(() => {
    const onMode = (event) => {
      if (event?.detail?.projectId && String(event.detail.projectId) !== String(projectId || "")) return;
      setModeTick((value) => value + 1);
    };
    const onMessages = (event) => {
      if (event?.detail?.projectId && String(event.detail.projectId) !== String(projectId || "")) return;
      if (Array.isArray(event?.detail?.messages)) {
        setLiveMessages(event.detail.messages);
      }
      if (event?.detail?.planDocument) {
        setLivePlanDoc((prev) => mergePlanDocuments(prev, event.detail.planDocument));
      }
    };
    const onWs = (event) => {
      const message = event?.detail;
      const wsProjectId = message?.ninfo?.projectId ?? message?.projectId;
      if (projectId && wsProjectId && String(projectId) !== String(wsProjectId)) return;
      const doc = extractPlanDocumentFromWs(message);
      if (doc && hasMeaningfulPlanDocument(doc)) {
        setLivePlanDoc((prev) => mergePlanDocuments(prev, doc));
      }
    };

    window.addEventListener(PLAN_WORKSPACE_MODE_EVENT, onMode);
    window.addEventListener(PLAN_CHAT_MESSAGES_EVENT, onMessages);
    window.addEventListener("chatMessageReceived", onWs);
    return () => {
      window.removeEventListener(PLAN_WORKSPACE_MODE_EVENT, onMode);
      window.removeEventListener(PLAN_CHAT_MESSAGES_EVENT, onMessages);
      window.removeEventListener("chatMessageReceived", onWs);
    };
  }, [projectId]);

  const planDocument = useMemo(() => {
    const fromMessages = buildPlanDocumentFromMessages(liveMessages);
    const fromDb = projectData?.plan?.planText
      ? projectData.plan
      : projectData?.planDocument ||
        projectData?.plan_document ||
        projectData?.metadata?.planDocument ||
        projectData?.projectInfo?.planDocument ||
        null;
    const fromSessionText = readPlanMeta(projectId)?.planText;
    const fromSession = fromSessionText ? { planText: fromSessionText } : null;
    return mergePlanDocuments(fromMessages, fromDb, fromSession, livePlanDoc);
  }, [liveMessages, livePlanDoc, projectData, projectId]);

  const progress = useMemo(() => getPlanWorkspaceProgress(planDocument), [planDocument]);
  const planId = resolvePlanId(projectData, liveMessages);
  const buildAvailable = resolveBuildAvailable(projectData, liveMessages);
  const showReady = Boolean(planId) && buildAvailable === true;
  const planning = !showReady;
  const erdChart = resolveErdChart(planDocument);
  const visibleTabs = TABS.filter(
    (item) => item.id !== "erd" || planning || erdChart
  );

  useEffect(() => {
    if (tab === "erd" && !planning && !erdChart) {
      setTab("overview");
    }
  }, [tab, planning, erdChart]);

  void modeTick;
  void isProjectInPlanMode;
  void isPlanAppType;
  void hasMeaningfulPlanDocument;

  const handleStartEditing = () => {
    setEditing(true);
  };

  const handleCancelEditing = () => {
    setEditing(false);
    setRefineInput("");
  };

  const handleSavePlan = async () => {
    const revision = refineInput.trim();
    if (!planId || !revision || typeof onUpdatePlan !== "function") return;
    const existingPlanText = resolvePlanBuildPrompt(projectData, liveMessages);
    setSaving(true);
    try {
      await onUpdatePlan({
        planId,
        additionalInput: revision,
        existingPlanText,
        plan: { id: planId, planText: existingPlanText, format: "markdown" },
      });
      setRefineInput("");
      setEditing(false);
    } catch {
      /* keep the text box so the user can retry */
    } finally {
      setSaving(false);
    }
  };

  const handleStartBuilding = () => {
    writePlanWorkspaceViewOpen(projectId, false);
    writePlanWorkspaceBuildMode(projectId, true);
    const composerApp = readComposerMode(projectId);
    const buildAppType =
      composerApp === "APP_AGENT" || composerApp === "WEBSITE" ? composerApp : "APP";
    writeComposerMode(projectId, buildAppType);
    const request = buildAppRequestFromPlan({ projectId, appType: buildAppType });
    onStartBuilding?.({
      planId,
      projectId,
      prompt: request.prompt,
      request,
    });
  };

  const handleCloseWorkspace = () => {
    writePlanWorkspaceViewOpen(projectId, false);
    writePlanWorkspaceBuildMode(projectId, true);
    // Keep current non-PLAN composer if set; default to APP preview.
    const current = readComposerMode(projectId);
    if (!current || current === "PLAN") {
      writeComposerMode(projectId, "APP");
    }
  };

  return (
    <div className={`sa-plan-ws ${showReady ? "is-ready" : "is-planning"} ${editing ? "is-editing" : ""}`}>
      <header className="sa-plan-ws-header">
        <div className="sa-plan-ws-title-row">
          {typeof onToggleChat === "function" ? (
            <button
              type="button"
              className="sa-plan-ws-chat-toggle"
              onClick={onToggleChat}
              title={isChatVisible ? "Hide chat" : "Show chat"}
              aria-label={isChatVisible ? "Hide chat" : "Show chat"}
            >
              <i className={`fa-solid ${isChatVisible ? "fa-angles-right" : "fa-angles-left"}`} aria-hidden="true" />
            </button>
          ) : null}
          <div className="sa-plan-ws-title">
            <span className="sa-plan-ws-kicker">
              <i className="fa-solid fa-list-check" aria-hidden="true" />
              Project Plan
              {projectData?.plan?.version ? (
                <span className="sa-plan-ws-version">v{projectData.plan.version}</span>
              ) : null}
            </span>
            <h1>{projectData?.name || "Untitled Project"}</h1>
            <p>
              {editing
                ? "Describe what to change. The current plan is sent with your note so the worker can update it in place."
                : showReady
                ? "Review the plan, add changes if needed, then start building when you’re ready."
                : saving
                ? "Updating the plan from your latest notes…"
                : "Live planning workspace — details fill in as the plan takes shape."}
            </p>
          </div>
        </div>
        <div className="sa-plan-ws-header-actions">
          <div className={`sa-plan-ws-badge ${showReady && !saving ? "is-ready" : ""} ${editing || saving ? "is-editing" : ""}`}>
            {saving ? "Updating" : editing ? "Editing" : showReady ? "Ready" : "Planning"}
          </div>
          <button
            type="button"
            className="sa-plan-ws-close"
            onClick={handleCloseWorkspace}
            title="Close plan workspace"
            aria-label="Close plan workspace"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </div>
      </header>

      <div className="sa-plan-ws-tabs" role="tablist" aria-label="Plan workspace sections">
        {visibleTabs.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            className={`sa-plan-ws-tab ${tab === item.id ? "is-active" : ""}`}
            onClick={() => setTab(item.id)}
          >
            <i className={item.icon} aria-hidden="true" />
            {item.label}
          </button>
        ))}
      </div>

      <div className="sa-plan-ws-body">
        {planning || saving ? <PlanningProgress progress={progress} /> : null}

        {tab === "overview" ? <OverviewTab doc={planDocument} /> : null}
        {tab === "architecture" ? <ArchitectureTab doc={planDocument} /> : null}
        {tab === "erd" ? <ErdTab doc={planDocument} /> : null}
        {tab === "agents" ? <AgentsTab doc={planDocument} /> : null}
        {tab === "implementation" ? <ImplementationTab doc={planDocument} /> : null}
        {tab === "credits" ? <CreditsTab doc={planDocument} /> : null}
        {tab === "details" ? <DetailsTab doc={planDocument} /> : null}
      </div>

      {showReady || editing ? (
        <div className={`sa-plan-ws-cta ${editing ? "is-editing" : ""}`}>
          {editing ? (
            <form
              className="sa-plan-ws-refine"
              onSubmit={(event) => {
                event.preventDefault();
                handleSavePlan();
              }}
            >
              <label className="sa-plan-ws-field sa-plan-ws-refine-field">
                <span>What should change in this plan?</span>
                <textarea
                  rows={4}
                  value={refineInput}
                  disabled={saving}
                  placeholder="Example: Add an admin export, use PostgreSQL, remove the password-reset email flow…"
                  onChange={(event) => setRefineInput(event.target.value)}
                />
              </label>
              <div className="sa-plan-ws-cta-row">
                <div className="sa-plan-ws-cta-copy">
                  <strong>
                    <i className="fa-solid fa-pen-to-square" aria-hidden="true" />
                    Edit Plan
                  </strong>
                  <span>Your note is sent with the current plan. The same plan ID is updated.</span>
                </div>
                <div className="sa-plan-ws-cta-actions">
                  <button type="button" className="sa-plan-ws-secondary-btn" onClick={handleCancelEditing} disabled={saving}>
                    Cancel
                  </button>
                  <button type="submit" className="sa-plan-ws-cta-btn" disabled={saving || !refineInput.trim()}>
                    {saving ? "Updating…" : "Update Plan"}
                  </button>
                </div>
              </div>
            </form>
          ) : (
            <>
              <div className="sa-plan-ws-cta-copy">
                <strong>
                  <i className="fa-solid fa-circle-check" aria-hidden="true" />
                  Plan Ready
                </strong>
                <span>Edit the plan with additional requirements, or click Build to continue in APP mode.</span>
              </div>
              <div className="sa-plan-ws-cta-actions">
                <button type="button" className="sa-plan-ws-secondary-btn" onClick={handleStartEditing} disabled={saving}>
                  <i className="fa-solid fa-pen" aria-hidden="true" />
                  Edit Plan
                </button>
                <button type="button" className="sa-plan-ws-cta-btn" onClick={handleStartBuilding} disabled={saving}>
                  Build
                  <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                </button>
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default PlanWorkspace;
